import React, { useState } from "react";
import { Paper, Typography, Grid2, IconButton, ListItemIcon, ListItemText, MenuItem, Menu } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { IconDotsVertical, IconEye, IconEdit, IconTrash, IconPlus } from "@tabler/icons-react";

import CustomButton from "@components/button/customButton";
import ConfirmDialog from "@components/dialog/confirmDialog";
import SearchTextField from "@components/inputs/searchTextField";

import { applyFilters, createSearchFilter } from "@utils/filter";

export default function QuestionListing({
  pageAction,
  setOpenAddEditQuestionDialog,
  setAddEditQuestionDialogType,
  setOpenViewQuestionDialog,
  selectedQuestion,
  setSelectedQuestion,
  questionListing,
  setQuestionListing,
  disabled,
}) {

  const [searchText, setSearchText] = useState("")

  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [openActionMenu, setOpenActionMenu] = useState(false);
  
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false)

  const onConfirmedDelete = () => {
    setQuestionListing((prev) => {
      // Find the index of the question to delete
      const questionIndex = prev.findIndex((q) => q.questionGuid === selectedQuestion?.questionGuid);
    
      if (questionIndex === -1) {
        console.error("Question not found");
        return prev; // Return the original state if the question isn't found
      }
    
      // Remove the question and adjust sequences of remaining questions
      const updatedList = prev
        .filter((q) => q.questionGuid !== selectedQuestion?.questionGuid)
        .map((q) => {
          const currentSequence = parseInt(q.sequence, 10);
          return currentSequence > questionIndex
            ? { ...q, sequence: currentSequence - 1 } // Adjust sequence after the removed question
            : q;
        });
    
      return updatedList;
    })
    setOpenConfirmDialog(false)
  }

  //================================Table================================

  const handleClickView = () => {
    setOpenViewQuestionDialog(true);
    setOpenActionMenu(false);
  }

  const handleClickEdit = () => {
    setAddEditQuestionDialogType("Edit");
    setOpenAddEditQuestionDialog(true);
    setOpenActionMenu(false);
  }

  const handleClickDelete = () => {
    setOpenConfirmDialog(true);
    setOpenActionMenu(false);
  }

  const handleClickActions = (event, row) => {
    setMenuAnchorEl(event.currentTarget);
    setOpenActionMenu(true);
    setSelectedQuestion(row);
  };

  const renderPopper = () => {
    return (
      <Menu
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        anchorEl={menuAnchorEl}
        open={openActionMenu}
        onClose={()=>setOpenActionMenu(false)}
      >
        <MenuItem
          onClick={handleClickView}
        >
          <ListItemIcon>
            <IconEye size={20}/>
          </ListItemIcon>
          <ListItemText>View</ListItemText>
        </MenuItem>
        {(["create", "edit"].includes(pageAction) && !disabled) && [
        <MenuItem
          key={"edit"}
          onClick={handleClickEdit}
        >
          <ListItemIcon>
            <IconEdit size={20}/>
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>,
        <MenuItem
          key={"delete"}
          onClick={handleClickDelete}
        >
          <ListItemIcon>
            <IconTrash size={20}/>
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
        ]}
      </Menu>
    );
  };

  //=====================================================================

  //================================Filter================================

  const filters = [
    createSearchFilter(searchText, []),
  ];

  const filteredData = applyFilters(questionListing, filters);

  //======================================================================

  return (
    <>
      <Paper
        sx={{
          p: "20px 25px 0px 25px",
          boxShadow: "none",
          borderRadius: "10px",
          border: "1px solid #E5E5E5",
        }}
      >
        <Grid2 container spacing={"20px"}>
          <Grid2 size={12}>
            <Typography fontSize={"0.9rem"} color="#202020" fontWeight={600}>Question(s)</Typography>
          </Grid2>
          <Grid2 size={{ lg: 6, md: 6, sm: 6, xs: "grow" }}>
            <SearchTextField 
              value={searchText}
              setValue={setSearchText}
            />
          </Grid2>
          <Grid2 size={{ lg: 6, md: 6, sm: 6, xs: "auto" }} sx={{ display:"flex", justifyContent: "flex-end", alignItems: "center" }}>
            {["create", "edit"].includes(pageAction) &&
            <CustomButton disabled={disabled} onClick={()=>{setAddEditQuestionDialogType('Add');setOpenAddEditQuestionDialog(true)}} startIcon={<IconPlus size={13} />}>
              Add
            </CustomButton>
            }
          </Grid2>
        </Grid2>
        <DataGrid
          // loading={isFetching}
          rows={filteredData}
          getRowId={(row) => row.questionGuid}
          columns={[
            {
              field: 'questionNumber',
              headerName: 'Question No',
              flex: 1, minWidth: 100,
            },
            {
              field: 'questionEng',
              headerName: 'Question',
              flex: 1.5, minWidth: 100,
            },
            {
              field: 'sequence',
              headerName: 'Sequence',
              flex: 1, minWidth: 100,
            },
            {
              field: 'data',
              headerName: 'Data',
              flex: 1, minWidth: 100,
              valueGetter: (value) => value || "-",
            },
            {
              field: 'createdOn',
              headerName: 'Add Date',
              flex: 1, minWidth: 100,
              valueGetter: (value) => value || "-",
            },
            {
              field: 'createdBy',
              headerName: 'Add By',
              flex: 1, minWidth: 100,
              valueGetter: (value) => value || "-",
            },
            {
              field: 'actions',
              headerName: 'Actions',
              sortable: false,
              renderCell: (params) => (
                <IconButton onClick={(event) => handleClickActions(event, params.row)}>
                  <IconDotsVertical />
                </IconButton>
              ),
            },
          ]}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 10,
              },
            },
          }}
          pageSizeOptions={[10,25,50]}
          disableRowSelectionOnClick
          disableColumnResize
          disableColumnMenu
          disableColumnSelector
          disableDensitySelector
          disableColumnSorting
          localeText={{ noRowsLabel: 'No records found' }}
          sx={{ border: 0 }}
        />
        {renderPopper()}
      </Paper>
      <ConfirmDialog
        open={openConfirmDialog}
        onClose={()=>setOpenConfirmDialog(false)}
        onSubmit={onConfirmedDelete}
        dialogTitle="Remove Question"
        dialogDescription="Are you sure you want to remove this question?"
        items={[selectedQuestion?.questionNumber]}
      />
    </>
  );
}
